import {
  faBed,
  faCar,
  faFlag,
  faMugHot,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AssignmentTypeEnum,
  RouteDriverSwapDTO,
  RouteFieldTesterSuggestionDTO,
  RouteLegDTO,
  RoutePackageDTO,
  RouteRebookingKindEnum,
  RouteRebookingProposalDTO,
} from 'api/model';
import useTranslations from 'contexts/basicData/useTranslations';
import React from 'react';
import styled from 'styled-components';
import useHasAccessToAbsoluteTb from 'hooks/useHasAccessToAbsoluteTb';
import useHasAccessToRoutePlanApply, {
  NO_ROUTE_PLAN_APPLY_ACCESS_TITLE,
} from 'hooks/useHasAccessToRoutePlanApply';
import GapRemedySelector from './GapRemedySelector';
import { legCostBasisMs } from './tb-language';
import { TbBadge } from './WarningPills';
import CaseRegLink from './CaseRegLink';
import {
  LegRebookingActions,
  LegRebookingConfirmedMark,
  LegRebookingConfirmedNote,
  LegRebookingContact,
  LegRebookingSpacer,
} from './LegRebooking';
import { ConfirmedRebooking, RebookingAnswer } from './rebooking-helpers';
import {
  describeEarlierDayDeparture,
  describeHubStopoverDestination,
  describePendingAcquisition,
  formatClock,
  formatDurationMs,
  GapChoice,
  hubHomewardMs,
  PackageSelection,
  selectedHubStopover,
} from './selection-helpers';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/**
 * Ett ben som väntar på kundens svar om ny tid (pending) eller redan fått ja (confirmed) tonas,
 * så att samtalen syns i rutten utan en egen panel.
 */
const Node = styled.div<{ rebooking?: 'pending' | 'confirmed' }>`
  display: flex;
  align-items: baseline;
  gap: 8px;
  ${({ rebooking }) =>
    rebooking &&
    `
    margin: 0 -6px;
    padding: 6px;
    border-radius: 4px;
    background: ${rebooking === 'confirmed' ? 'rgb(241, 248, 243)' : 'rgb(253, 246, 239)'};
  `}
`;

const NodeIcon = styled.span<{ tone?: 'home' | 'hotel' | 'assignment' }>`
  width: 18px;
  text-align: center;
  color: ${({ tone }) =>
    tone === 'hotel'
      ? 'rgb(0,122,255)'
      : tone === 'home'
        ? '#000'
        : 'rgb(204,102,51)'};
`;

const NodeBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

/**
 * Löpnumret på benet. Samma siffra sätts på motsvarande sträcka i kartan (RouteMap), så att
 * raden i kortet och linjen på kartan går att para ihop — utan den går det inte att se vilken
 * sträcka som är vilken när ett paket har flera ben.
 */
const LegNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-right: 6px;
  border-radius: 50%;
  background: #444;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  vertical-align: middle;
`;

const NodeTitle = styled.div`
  font-weight: 700;
`;

const NodeDetail = styled.div`
  font-size: 0.88em;
  color: rgb(90, 90, 90);
`;

const NodeTime = styled.div<{ late?: boolean }>`
  text-align: right;
  white-space: nowrap;
  font-weight: 600;
  color: ${({ late }) => (late ? 'rgb(255,0,0)' : 'inherit')};
`;

const TimeSub = styled.div`
  font-size: 0.8em;
  color: rgb(120, 120, 120);
  font-weight: 400;
  text-align: right;
`;

const Transfer = styled.div`
  margin-left: 26px;
  font-size: 0.85em;
  color: rgb(140, 140, 140);
  border-left: 2px dotted rgb(190, 190, 190);
  padding-left: 10px;
`;

/**
 * Rasten ligger INNE i benets fönster (plannedEnd inkluderar den), så raden hör till noden och
 * ritas i dess kropp — inte som ett eget steg efter benet. Samma streckade språk som
 * överföringsraden, men i rast-grönt så den inte läses som restid.
 */
const BreakRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.85em;
  color: rgb(0, 110, 0);
  border-left: 2px dotted rgba(0, 150, 0, 0.45);
  padding-left: 8px;
`;

/**
 * "Bilen är inte köpt än" (TL-feedback 2026-09-30, paket #4). Amber när någon annan köper
 * bilen — då är det risken trafikledaren ska väga — grå när den valda fälttestaren själv köper.
 */
const PendingAcquisitionRow = styled.div<{ risky: boolean }>`
  margin-top: 3px;
  font-size: 11px;
  color: ${({ risky }) => (risky ? 'rgb(133, 79, 11)' : 'rgb(140, 140, 140)')};
`;

const CaseTypeTag = styled.span`
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 3px;
  background: rgb(235, 235, 235);
  color: rgb(90, 90, 90);
  font-size: 0.8em;
  font-weight: 600;
`;

// Mellanlandad bil: bilen står redan parkerad på platsen och väntar, till skillnad från ett
// uppdrag med en kundtid. Casetypstaggen kan inte visa det — ett mellanlandat inbyte skrivs
// annars bara som "Inbyte". Egen färg så att den inte läses som ännu en casetyp.
const StopoverTag = styled.span`
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 3px;
  background: rgb(225, 238, 250);
  color: rgb(21, 87, 143);
  font-size: 0.8em;
  font-weight: 600;
`;

const LowRangeTag = styled.span`
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 3px;
  background: #fef3c7;
  color: #854d0e;
  font-size: 0.8em;
  font-weight: 600;
`;

const LowRangeElectricTag = ({ leg }: { leg: RouteLegDTO }) =>
  leg.isLowRangeElectric ? (
    <LowRangeTag title="Elbil med dålig räckvidd – rekommenderas lastbil">
      Dålig räckvidd
    </LowRangeTag>
  ) : null;

const isStopoverCar = (leg: RouteLegDTO) =>
  leg.assignmentTypeID === AssignmentTypeEnum.FurtherTransport ||
  leg.assignmentTypeID === AssignmentTypeEnum.PlannedFurtherTransport;

/**
 * Luckans längd — föregående bens slut till nästa bens start — och sträckan. Restiden står redan
 * på överföringsraden ovanför; det planeraren behöver vid väljaren är hur stort hålet i dagen är.
 */
const describeTransferGap = (
  previous: RouteLegDTO,
  next: RouteLegDTO
): string => {
  const gapMs = Math.max(
    0,
    new Date(next.plannedStart).getTime() -
      new Date(previous.plannedEnd).getTime()
  );
  return `${formatDurationMs(gapMs)} (${formatClock(previous.plannedEnd)}–${formatClock(next.plannedStart)}) · ${next.transferFromPreviousKm} km`;
};

const EstimateBadge = styled.span`
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 3px;
  background: rgb(235, 235, 235);
  font-size: 0.85em;
`;

/** Uppdragets riktiga slutmål, som halvan inte längre visar. */
const DestinationChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
  padding: 2px 7px;
  border-radius: 3px;
  background: rgb(253, 243, 219);
  color: rgb(133, 79, 11);
  font-size: 11px;
`;

const SwapSubDetail = styled.div`
  margin-top: 3px;
  font-size: 11px;
  color: rgb(140, 140, 140);
`;

const HandoverNode = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-left: 26px;
  padding: 6px 9px;
  border-radius: 4px;
  background: rgb(233, 241, 251);
  color: rgb(24, 95, 165);
  font-size: 12px;
`;

const HandoverTitle = styled.span`
  font-weight: 600;
`;

/**
 * Låset hör hemma här och inte bara i den manuella listan: uppdraget trafikledaren vill hålla
 * undan ligger per definition i ett paket automatiken redan lagt det i (mötets P4 — inköpet som
 * var någon annans hemresa). Är det inte synligt där det syns, hittas det inte i tid.
 */
/** Bär placeringen och titeln: en avstängd knapp får inga mushändelser, så titeln sitter här. */
const ReserveLegWrap = styled.span`
  margin-top: 2px;
  align-self: flex-start;
`;

const ReserveLegButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.8em;
  color: rgb(30, 90, 160);
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    color: rgb(150, 150, 150);
    text-decoration: none;
    cursor: not-allowed;
  }
`;

interface Props {
  pkg: RoutePackageDTO;
  /** Saknas kandidat visas ändå alltid uppdragsbenen — bara hem-/pendlingsnoder och gap-val döljs. */
  candidate?: RouteFieldTesterSuggestionDTO;
  selection: PackageSelection;
  onGapChoice: (gap: 'first' | 'last', choice: GapChoice) => void;
  /**
   * Val för en intern överföring. Nycklas på benet överföringen leder TILL, eftersom ett paket
   * kan ha flera överföringar över tröskeln.
   */
  onTransferGapChoice?: (assignmentId: number, choice: GapChoice) => void;
  applyDisabled?: boolean;
  selectedAnnonsByDestination?: Record<number, number>;
  /**
   * Skiftet ersätter hemresan: föraren kör motpartens fordon hem, så sista-bens-luckan finns
   * inte att lösa. Utan detta visade kortet ett tågval hem för 1 704 kr som aldrig blir av —
   * och valet var dessutom dött, eftersom buildPackageActions kastar det när ett skifte finns.
   */
  driverSwap?: RouteDriverSwapDTO;
  /** Låser ett enskilt ben för manuell planering. Utelämnas där låset inte är aktuellt. */
  onReserveLeg?: (leg: RouteLegDTO) => void;
  /** Till sista luckans väljare: varför samåkningen spärras eller gruppen inte gäller. */
  homeRideNotice?: string;
  /** Ombokningsförslagen hamnar på sina ben. Utan den visas inga förslag. */
  rebooking?: LegRebookingProps;
}

export interface LegRebookingProps {
  /** Saknar skrivrätt (global inställning 106) — knapparna visas men är avstängda. */
  disabled: boolean;
  disabledTitle?: string;
  /** Uppdraget vars svar just nu skickas. */
  busyAssignmentId?: number;
  /** Ja som redan är sparade i den här körningen, per uppdrag. */
  confirmed: Record<number, ConfirmedRebooking>;
  onAnswer: (proposal: RouteRebookingProposalDTO, answer: RebookingAnswer) => void;
}

/** Givarens ben i en ifylld kort dag: det kommer från ett annat paket i körningen. */
const MergedTag = styled.span`
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 3px;
  background: rgb(251, 239, 230);
  color: rgb(138, 75, 18);
  font-size: 0.8em;
  font-weight: 600;
`;

const BookedToChange = styled.span`
  color: rgb(138, 75, 18);
  font-weight: 600;

  s {
    font-weight: 400;
  }
`;

const PendingAcquisitionNote: React.FC<{
  leg: RouteLegDTO;
  packageLegs: RouteLegDTO[];
  selectedCandidateUserId: number | null;
}> = ({ leg, packageLegs, selectedCandidateUserId }) => {
  const note = describePendingAcquisition(
    leg,
    packageLegs,
    selectedCandidateUserId
  );
  return note ? (
    <PendingAcquisitionRow risky={note.risky}>
      {note.text}
    </PendingAcquisitionRow>
  ) : null;
};

/**
 * Vertikal tidslinje: startmarkör (hem eller övernattning) → uppdragsben med
 * överföringar emellan → ev. gap-slots (första/sista benet) → hemmarkör.
 */
const LegTimeline: React.FC<Props> = ({
  pkg,
  candidate,
  driverSwap,
  selection,
  onGapChoice,
  onTransferGapChoice,
  applyDisabled,
  selectedAnnonsByDestination,
  onReserveLeg,
  homeRideNotice,
  rebooking,
}) => {
  const translations = useTranslations();
  const showAbsoluteTb = useHasAccessToAbsoluteTb();
  // Att låsa ett uppdrag skriver till databasen, så det kräver samma behörighet som verkställ.
  const canReserve = useHasAccessToRoutePlanApply();
  const firstLeg = pkg.legs[0];
  const lastLeg = pkg.legs[pkg.legs.length - 1];
  // Vald mellanlandning på sista benet: benet körs bara till hubben, resten lämnas oplanerat.
  const hubStopover = selectedHubStopover(candidate, selection, driverSwap);
  const hubLegId = hubStopover ? lastLeg?.assignmentId : undefined;

  // Ett sparat ja vinner över förslaget: misslyckas omräkningen efter svaret ligger förslaget
  // kvar på paketet, men tiden är redan flyttad och får inte bekräftas en gång till.
  const proposals = rebooking
    ? (pkg.rebookingProposals ?? []).filter(
        (r) => rebooking.confirmed[r.assignmentId] === undefined
      )
    : [];
  const proposalFor = (assignmentId: number) =>
    proposals.find((r) => r.assignmentId === assignmentId);
  const confirmedFor = (assignmentId: number) =>
    rebooking?.confirmed[assignmentId];
  const legNumberOf = (assignmentId?: number) => {
    const index = pkg.legs.findIndex((l) => l.assignmentId === assignmentId);
    return index >= 0 ? index + 1 : undefined;
  };
  // Svarskolumnen sätts på alla ben när någon behöver den, så att tiderna linjerar.
  const hasRebookingColumn =
    proposals.length > 0 ||
    pkg.legs.some((l) => confirmedFor(l.assignmentId) !== undefined);
  // Förslag vars ben inte ritas här (hemresepasset släpper dem, men säkrast att inte tappa ett).
  const unplacedProposals = proposals.filter(
    (r) => !pkg.legs.some((l) => l.assignmentId === r.assignmentId)
  );
  const fillProposal = proposals.find(
    (r) => r.kind === RouteRebookingKindEnum.FillShortDay
  );
  const mergedFromPackage = (assignmentId: number) =>
    fillProposal?.mergedPackageIndex != null &&
    fillProposal.donorAssignmentIds.includes(assignmentId)
      ? fillProposal.mergedPackageIndex + 1
      : undefined;

  const mergedTag = (assignmentId: number) => {
    const fromPackage = mergedFromPackage(assignmentId);
    return fromPackage ? (
      <MergedTag title="Lades till dagen av ombokningsförslaget">
        Från paket {fromPackage}
      </MergedTag>
    ) : null;
  };

  const rebookingTone = (assignmentId: number) =>
    proposalFor(assignmentId)
      ? 'pending'
      : confirmedFor(assignmentId)
        ? 'confirmed'
        : undefined;

  const rebookingBody = (assignmentId: number) => {
    const proposal = proposalFor(assignmentId);
    if (proposal) {
      return (
        <LegRebookingContact
          pairedLegNumber={legNumberOf(proposal.pairedAssignmentId)}
          proposal={proposal}
        />
      );
    }
    const confirmed = confirmedFor(assignmentId);
    return confirmed ? <LegRebookingConfirmedNote confirmed={confirmed} /> : null;
  };

  const rebookingColumn = (assignmentId: number) => {
    if (!rebooking || !hasRebookingColumn) return null;
    const proposal = proposalFor(assignmentId);
    if (proposal) {
      return (
        <LegRebookingActions
          key={`${assignmentId}-${new Date(proposal.proposedBookedTo).getTime()}`}
          busy={rebooking.busyAssignmentId === assignmentId}
          disabled={rebooking.disabled}
          disabledTitle={rebooking.disabledTitle}
          onAnswer={rebooking.onAnswer}
          proposal={proposal}
        />
      );
    }
    return confirmedFor(assignmentId) ? (
      <LegRebookingConfirmedMark />
    ) : (
      <LegRebookingSpacer />
    );
  };

  const proposedBookedTo = (proposal: RouteRebookingProposalDTO) => (
    <BookedToChange>
      Bokad till <s>{formatClock(proposal.currentBookedTo)}</s>{' '}
      {formatClock(proposal.proposedBookedTo)}
    </BookedToChange>
  );

  const bookedToText = (leg: RouteLegDTO) => {
    const proposal = proposalFor(leg.assignmentId);
    return proposal ? (
      proposedBookedTo(proposal)
    ) : (
      <>Bokad till {formatClock(leg.bookedTo)}</>
    );
  };

  // Vilken sida av bytet paketet är. `mine` är den här förarens halvor, `other` motpartens —
  // och det är motpartens fordon som körs hem, så korsningen måste hållas rätt.
  const swapSides = driverSwap
    ? driverSwap.sideA.packageIndex === pkg.packageIndex
      ? { mine: driverSwap.sideA, other: driverSwap.sideB }
      : { mine: driverSwap.sideB, other: driverSwap.sideA }
    : undefined;

  return (
    <Wrapper>
      {unplacedProposals.map((proposal) => (
        <Node key={`rebooking-${proposal.assignmentId}`} rebooking="pending">
          <NodeIcon tone="assignment">
            <FontAwesomeIcon icon={faCar} />
          </NodeIcon>
          <NodeBody>
            <NodeTitle>
              <CaseRegLink caseId={proposal.caseId}>
                {proposal.registrationNumber ||
                  `Uppdrag ${proposal.assignmentId}`}
              </CaseRegLink>
            </NodeTitle>
            <LegRebookingContact proposal={proposal} />
          </NodeBody>
          <NodeTime>
            <TimeSub>{proposedBookedTo(proposal)}</TimeSub>
          </NodeTime>
          {rebookingColumn(proposal.assignmentId)}
        </Node>
      ))}
      {candidate &&
        (candidate.startsAfterAssignmentId ? (
          /* Föraren har redan ett uppdrag i dag som slutar i tid: dagen börjar där det slutar,
             inte hemma. Uppdraget är redan planerat och ritas inte som ett ben här. */
          <Node>
            <NodeIcon tone="assignment">
              <FontAwesomeIcon icon={faCar} />
            </NodeIcon>
            <NodeBody>
              <NodeTitle>
                Efter befintligt uppdrag · {candidate.stayoverCity}
              </NodeTitle>
              <NodeDetail>{candidate.stayoverAddress}</NodeDetail>
            </NodeBody>
          </Node>
        ) : candidate.startsFromStayover ? (
          <Node>
            <NodeIcon tone="hotel">
              <FontAwesomeIcon icon={faBed} />
            </NodeIcon>
            <NodeBody>
              <NodeTitle>Övernattning · {candidate.stayoverCity}</NodeTitle>
              <NodeDetail>{candidate.stayoverAddress}</NodeDetail>
            </NodeBody>
          </Node>
        ) : (
          /* Hemankaret är ett PLANERINGSUNDERLAG, inte ett stopp på rutten — dagen räknas
             från det, men fälttestaren "besöker" det inte. Det stod tidigare som egna noder
             överst och nederst, vilket gjorde det till tredje och fjärde gången samma adress
             visades: rubriken i PackageCard skriver redan ut ankaret. Kvar är pendlingstiden,
             som är det enda planeraren faktiskt behöver härifrån. */
          candidate.commuteToFirst && (
            <Transfer>
              ~{formatDurationMs(candidate.commuteToFirst.durationMs)} pendling
              till första upphämtningen · {candidate.commuteToFirst.distanceKm}{' '}
              km
              {candidate.commuteToFirst.isEstimateOnly && (
                <EstimateBadge>uppskattad</EstimateBadge>
              )}
            </Transfer>
          )
        ))}

      {candidate?.firstLegGap && firstLeg && (
        <GapRemedySelector
          disabled={applyDisabled}
          earlierDayDeparture={describeEarlierDayDeparture(
            pkg.effectiveWindowStart,
            pkg.windowStart
          )}
          gap={candidate.firstLegGap}
          selectedAnnonsByDestination={selectedAnnonsByDestination}
          onChange={(choice) => onGapChoice('first', choice)}
          subtitle={
            candidate.commuteToFirst
              ? `${candidate.commuteToFirst.distanceKm} km`
              : undefined
          }
          title={`hem → första upphämtning (${firstLeg.fromCity})`}
          value={selection.firstGapChoice}
        />
      )}

      {swapSides &&
        pkg.legs.length === 1 &&
        (() => {
          const leg = pkg.legs[0];
          const { mine, other } = swapSides;
          return (
            <React.Fragment key={`swap-${leg.assignmentId}`}>
              <Node rebooking={rebookingTone(leg.assignmentId)}>
                <NodeIcon tone="assignment">
                  <FontAwesomeIcon icon={faCar} />
                </NodeIcon>
                <NodeBody>
                  <NodeTitle>
                    <CaseRegLink caseId={leg.caseId}>
                      {leg.registrationNumber || `Uppdrag ${leg.assignmentId}`}
                    </CaseRegLink>
                    <CaseTypeTag>
                      {translations.caseTypes?.[leg.caseTypeID]}
                    </CaseTypeTag>
                    <LowRangeElectricTag leg={leg} />
                    {isStopoverCar(leg) && (
                      <StopoverTag
                        title={`Bilen står redan på ${leg.fromAddress}, ${leg.fromCity}`}
                      >
                        Mellanlandad bil
                      </StopoverTag>
                    )}
                  </NodeTitle>
                  <NodeDetail>
                    {leg.fromAddress}, {leg.fromCity} → {driverSwap?.pointName},{' '}
                    {driverSwap?.pointCity}
                  </NodeDetail>
                  {rebookingBody(leg.assignmentId)}
                  {/* Halvan slutar vid bytespunkten, så raden visar inte längre uppdragets
                      slutmål. Utan den här märkningen läste kortet som om bilen blev kvar i
                      Jönköping. */}
                  <DestinationChip>
                    <FontAwesomeIcon icon={faFlag} />
                    slutmål {mine.toCity}
                    {other.fieldTesterName
                      ? ` — körs vidare av ${other.fieldTesterName}`
                      : ' — körs vidare av motparten'}
                  </DestinationChip>
                </NodeBody>
                <NodeTime>
                  {formatClock(mine.departureTime)}–
                  {formatClock(driverSwap!.meetingTime)}
                  {mine.departureShiftMinutes > 0 && (
                    <TimeSub>
                      avgången framflyttad {mine.departureShiftMinutes} min för
                      att synka mötet
                    </TimeSub>
                  )}
                </NodeTime>
                {rebookingColumn(leg.assignmentId)}
              </Node>

              <HandoverNode>
                <HandoverTitle>
                  ⇄ Byte — lämnar{' '}
                  <CaseRegLink caseId={mine.caseId}>
                    {mine.registrationNumber}
                  </CaseRegLink>
                  , tar{' '}
                  <CaseRegLink caseId={other.caseId}>
                    {mine.returnRegistrationNumber}
                  </CaseRegLink>
                </HandoverTitle>
                <span>
                  {formatClock(driverSwap!.meetingTime)}–
                  {formatClock(driverSwap!.handoverEndTime)}
                </span>
                {hasRebookingColumn && <LegRebookingSpacer />}
              </HandoverNode>

              <Node>
                <NodeIcon tone="assignment">
                  <FontAwesomeIcon icon={faCar} />
                </NodeIcon>
                <NodeBody>
                  <NodeTitle>
                    {mine.returnRegistrationNumber || 'Motpartens fordon'}
                  </NodeTitle>
                  <NodeDetail>
                    {driverSwap?.pointName}, {driverSwap?.pointCity} →{' '}
                    {mine.returnToCity}
                  </NodeDetail>
                  <SwapSubDetail>
                    hämtades i {other.fromCity}
                    {other.fieldTesterName
                      ? ` av ${other.fieldTesterName}`
                      : ''}
                  </SwapSubDetail>
                </NodeBody>
                <NodeTime>
                  {formatClock(driverSwap!.handoverEndTime)}–
                  {formatClock(mine.returnArrivalTime)}
                </NodeTime>
                {hasRebookingColumn && <LegRebookingSpacer />}
              </Node>
            </React.Fragment>
          );
        })()}

      {!(swapSides && pkg.legs.length === 1) &&
        pkg.legs.map((leg, index) => (
          <React.Fragment key={leg.assignmentId}>
            {index > 0 && leg.transferFromPreviousMs > 0 && (
              <Transfer>
                ~{formatDurationMs(leg.transferFromPreviousMs)} överföring ·{' '}
                {leg.transferFromPreviousKm} km
              </Transfer>
            )}
            {/* Väljaren för överföringen. Backend sätter transferGap när överföringens
              RESTID överstiger tröskeln (bufferten borträknad), så raden dyker inte upp på
              varje promenad mellan två portar — eller när föraren väntar länge före benet
              (håltimme), och då handlar väljaren om att fylla tiden. */}
            {leg.transferGap && onTransferGapChoice && (
              <GapRemedySelector
                disabled={applyDisabled}
                gap={leg.transferGap}
                onChange={(choice) =>
                  onTransferGapChoice(leg.assignmentId, choice)
                }
                selectedAnnonsByDestination={selectedAnnonsByDestination}
                subtitle={describeTransferGap(pkg.legs[index - 1], leg)}
                title={
                  leg.transferGap.isIdleWaitOnly
                    ? `${leg.transferGap.idleWaitMinutes} min väntan före ${leg.fromCity}`
                    : leg.transferGap.idleWaitMinutes != null
                      ? `överföring → ${leg.fromCity} (${leg.transferGap.idleWaitMinutes} min väntan)`
                      : `överföring → ${leg.fromCity}`
                }
                value={
                  selection.transferGapChoices?.[leg.assignmentId] ?? 'none'
                }
              />
            )}
            <Node rebooking={rebookingTone(leg.assignmentId)}>
              <NodeIcon tone="assignment">
                <FontAwesomeIcon icon={faCar} />
              </NodeIcon>
              <NodeBody>
                <NodeTitle>
                  <LegNumber
                    title={`Ben ${index + 1} — samma siffra på kartan`}
                  >
                    {index + 1}
                  </LegNumber>
                  <CaseRegLink caseId={leg.caseId}>
                    {leg.registrationNumber || `Uppdrag ${leg.assignmentId}`}
                  </CaseRegLink>
                  <CaseTypeTag>
                    {translations.caseTypes?.[leg.caseTypeID]}
                  </CaseTypeTag>
                  {mergedTag(leg.assignmentId)}{' '}
                  <LowRangeElectricTag leg={leg} />
                  {isStopoverCar(leg) && (
                    <StopoverTag
                      title={`Bilen står redan på ${leg.fromAddress}, ${leg.fromCity}`}
                    >
                      Mellanlandad bil
                    </StopoverTag>
                  )}{' '}
                  <TbBadge
                    bufferPercent={leg.tbBufferPercent}
                    compact
                    costBasisMs={legCostBasisMs(leg)}
                    costSek={leg.costSek}
                    durationIsEstimated={leg.durationIsEstimated}
                    estimated={leg.tbIsEstimated}
                    level={leg.tbLevel}
                    revenueSek={leg.revenueSek}
                    scope="leg"
                    showAbsolute={showAbsoluteTb}
                  />
                  {/* Prislistan saknar rad: intäkten är lägsta pris på samma sträcka. Bara för
                    planeringens TB — aldrig fakturaunderlag. Backend skriver varför i noten. */}
                  {leg.revenueNote && (
                    <StopoverTag title={leg.revenueNote}>
                      Schablonpris
                    </StopoverTag>
                  )}
                  {/* Mellanlandning mitt i paketet: benet körs bara till platsen, resten lämnas
                    oplanerat när paketet verkställs, och nästa ben börjar därifrån. */}
                  {leg.stopoverName && (
                    <StopoverTag
                      title={`Bilen ställs på ${leg.stopoverName}. ${Math.round(leg.stopoverRemainingKm ?? 0)} km kvar till slutmålet lämnas oplanerat och blir en mellanlandad bil.`}
                    >
                      Mellanlandas
                    </StopoverTag>
                  )}
                  {/* Vald mellanlandning på sista benet: samma märkning som mitt på dagen. */}
                  {hubStopover && leg.assignmentId === hubLegId && (
                    <StopoverTag
                      title={`Bilen ställs på ${hubStopover.hubName}. ${Math.round(hubStopover.kmRemaining)} km kvar till ${leg.toCity} lämnas oplanerat och blir en mellanlandad bil.`}
                    >
                      Mellanlandas
                    </StopoverTag>
                  )}
                </NodeTitle>
                <NodeDetail>
                  {hubStopover && leg.assignmentId === hubLegId ? (
                    <>
                      {leg.fromAddress}, {leg.fromCity} →{' '}
                      {describeHubStopoverDestination(hubStopover)}
                    </>
                  ) : (
                    <>
                      {leg.fromAddress}, {leg.fromCity} → {leg.toAddress},{' '}
                      {leg.toCity}
                    </>
                  )}
                </NodeDetail>
                {rebookingBody(leg.assignmentId)}
                <PendingAcquisitionNote
                  leg={leg}
                  packageLegs={pkg.legs}
                  selectedCandidateUserId={selection.candidateUserId}
                />
                {onReserveLeg && (
                  <ReserveLegWrap
                    title={
                      canReserve
                        ? 'Uteslut uppdraget ur poolen och planera det för hand'
                        : NO_ROUTE_PLAN_APPLY_ACCESS_TITLE
                    }
                  >
                    <ReserveLegButton
                      disabled={!canReserve}
                      onClick={() => onReserveLeg(leg)}
                      type="button"
                    >
                      Lås för manuell planering
                    </ReserveLegButton>
                  </ReserveLegWrap>
                )}
                {leg.breakMinutes > 0 && (
                  <BreakRow
                    title={`Rasten är reserverad inne i det här uppdragets fönster: benet slutar ${leg.breakMinutes} min senare och allt efter det i kedjan skjuts fram lika mycket. Räknas i spannet, men inte som arbetstid eller kostnad.`}
                  >
                    <FontAwesomeIcon icon={faMugHot} /> Rast {leg.breakMinutes}{' '}
                    min
                  </BreakRow>
                )}
              </NodeBody>
              {/* Planerat slut EFTER bokad till-tid är normalt (lossning ingår) — "sen" =
                kedjan tvingar uppdraget att starta senare än sitt önskeläge. */}
              <NodeTime late={leg.lateMinutes > 0}>
                {formatClock(leg.plannedStart)}–
                {formatClock(
                  hubStopover && leg.assignmentId === hubLegId
                    ? hubStopover.arrivalAtHub
                    : leg.plannedEnd
                )}
                {leg.durationIsEstimated && (
                  <EstimateBadge>tid uppskattad</EstimateBadge>
                )}
                <TimeSub>
                  {bookedToText(leg)}
                  {leg.lateMinutes > 0 &&
                    ` — startar ${leg.lateMinutes} min sent`}
                </TimeSub>
              </NodeTime>
              {rebookingColumn(leg.assignmentId)}
            </Node>
          </React.Fragment>
        ))}

      {candidate?.lastLegGap && lastLeg && !driverSwap && (
        <GapRemedySelector
          disabled={applyDisabled}
          gap={candidate.lastLegGap}
          homeRideNotice={homeRideNotice}
          selectedAnnonsByDestination={selectedAnnonsByDestination}
          onChange={(choice) => onGapChoice('last', choice)}
          subtitle={
            candidate.returnFromLast
              ? `${candidate.returnFromLast.distanceKm} km`
              : undefined
          }
          title={
            candidate.endsBeforeAssignmentId
              ? `sista avlämning (${lastLeg.toCity}) → befintligt uppdrag (${
                  candidate.endsBeforeCity ?? ''
                }${
                  candidate.endsBeforeStart
                    ? ` ${formatClock(candidate.endsBeforeStart)}`
                    : ''
                })`
              : `sista avlämning (${lastLeg.toCity}) → hem`
          }
          value={selection.lastGapChoice}
        />
      )}

      {/* Samma sak i andra änden: ankaret självt är borta, hemresan kvar. Efter ett skifte
          gäller inte den odelade dagens hemresa — föraren står i returbenets slutort, inte vid
          uppdragets mål — så då skrivs ankomsten ut i stället för en sträcka vi inte känner. */}
      {candidate &&
        (swapSides ? (
          <Transfer>
            tillbaka i {swapSides.mine.returnToCity}{' '}
            {formatClock(swapSides.mine.returnArrivalTime)}
          </Transfer>
        ) : hubStopover && !candidate.endsBeforeAssignmentId ? (
          // Bilen står på hubben, så hemresan går därifrån (hubbförslagets egen hemkomst).
          <Transfer>
            ~
            {formatDurationMs(hubHomewardMs(hubStopover))}{' '}
            hemresa från {hubStopover.hubName} · hemma{' '}
            {formatClock(hubStopover.arrivalHomeWithStopover)}
          </Transfer>
        ) : (
          candidate.returnFromLast && (
            <Transfer>
              ~{formatDurationMs(candidate.returnFromLast.durationMs)}{' '}
              {candidate.endsBeforeAssignmentId
                ? 'till nästa uppdrag'
                : 'hemresa'}{' '}
              · {candidate.returnFromLast.distanceKm} km
              {candidate.returnFromLast.isEstimateOnly && (
                <EstimateBadge>uppskattad</EstimateBadge>
              )}
            </Transfer>
          )
        ))}

      {/* Spegeln av startnoden: föraren har redan ett uppdrag senare i dag och ska dit, inte hem.
          Uppdraget är redan planerat och ritas inte som ett ben här. */}
      {candidate?.endsBeforeAssignmentId && !swapSides && (
        <Node>
          <NodeIcon tone="assignment">
            <FontAwesomeIcon icon={faCar} />
          </NodeIcon>
          <NodeBody>
            <NodeTitle>
              Före befintligt uppdrag · {candidate.endsBeforeCity}
            </NodeTitle>
            <NodeDetail>{candidate.endsBeforeAddress}</NodeDetail>
          </NodeBody>
          {candidate.endsBeforeStart && (
            <NodeTime>
              <TimeSub>{formatClock(candidate.endsBeforeStart)}</TimeSub>
            </NodeTime>
          )}
        </Node>
      )}
    </Wrapper>
  );
};

export default LegTimeline;
